import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatDate } from '@/lib/utils/formatters';
import { useCreditoDaNota } from '@/services/CreditoIbsCbs';
import { rotuloUso } from '@/features/configuracoes/components/sefaz/regras-ibs-cbs';
import { rotuloTratamento, totaisDoCredito } from './credito-da-nota';

interface CreditoIbsCbsDaNotaProps {
  chaveAcesso: string;
}

const NUM = 'font-mono tabular-nums text-right text-sm';

export function CreditoIbsCbsDaNota({ chaveAcesso }: CreditoIbsCbsDaNotaProps) {
  const { data, isLoading, isError } = useCreditoDaNota(chaveAcesso, true);

  if (isLoading) {
    return <Skeleton className="h-24 w-full" aria-label="Carregando o crédito de IBS e CBS" />;
  }

  if (isError || !data?.status.resultado) {
    return (
      <p className="text-sm text-destructive">
        Não foi possível carregar o crédito de IBS e CBS desta nota.
      </p>
    );
  }

  const credito = data.data;
  if (!credito) {
    return <p className="text-sm text-muted-foreground">{data.status.mensagem}</p>;
  }

  const totais = totaisDoCredito(credito.Itens);

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Versão {credito.Versao} liberada pelo Recebimento · calculado em{' '}
        {formatDate(credito.CalculadoEm)}
        {credito.Devolucao && ' · devolução de venda: estorno do débito da venda original'}
      </p>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-12">Item</TableHead>
              <TableHead className="w-28">Regra</TableHead>
              <TableHead className="w-28">Uso</TableHead>
              <TableHead className="w-32">Tratamento</TableHead>
              <TableHead className="text-right">IBS destacado</TableHead>
              <TableHead className="text-right">IBS a apropriar</TableHead>
              <TableHead className="text-right">IBS disponível</TableHead>
              <TableHead className="text-right">CBS destacado</TableHead>
              <TableHead className="text-right">CBS a apropriar</TableHead>
              <TableHead className="text-right">CBS disponível</TableHead>
              {credito.Devolucao && <TableHead className="text-right">Estorno IBS / CBS</TableHead>}
              <TableHead className="min-w-[220px]">Motivo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {credito.Itens.map(item => (
              <TableRow key={item.NumItem}>
                <TableCell className="font-mono text-sm">{item.NumItem}</TableCell>
                <TableCell className="text-sm">
                  {item.Regra ?? '—'}
                  {item.ClassTrib && (
                    <span className="block text-xs text-muted-foreground font-mono">
                      {item.CstIbsCbs}/{item.ClassTrib}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{rotuloUso(item.Uso)}</TableCell>
                <TableCell className="text-sm">
                  {rotuloTratamento(item.TratamentoIbs, item.TratamentoCbs)}
                </TableCell>
                <TableCell className={NUM}>{formatCurrency(item.DestacadoIbs)}</TableCell>
                <TableCell className={NUM}>{formatCurrency(item.AApropriarIbs)}</TableCell>
                <TableCell className={`${NUM} font-semibold`}>
                  {formatCurrency(item.DisponivelIbs)}
                </TableCell>
                <TableCell className={NUM}>{formatCurrency(item.DestacadoCbs)}</TableCell>
                <TableCell className={NUM}>{formatCurrency(item.AApropriarCbs)}</TableCell>
                <TableCell className={`${NUM} font-semibold`}>
                  {formatCurrency(item.DisponivelCbs)}
                </TableCell>
                {credito.Devolucao && (
                  <TableCell className={NUM}>
                    {formatCurrency(item.EstornoIbs)} / {formatCurrency(item.EstornoCbs)}
                  </TableCell>
                )}
                <TableCell className="text-xs text-muted-foreground">
                  {item.Motivo ?? '—'}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted/50 font-semibold">
              <TableCell colSpan={3} className="text-sm">
                Total
              </TableCell>
              <TableCell className={NUM}>{formatCurrency(totais.destacadoIbs)}</TableCell>
              <TableCell className={NUM}>{formatCurrency(totais.aApropriarIbs)}</TableCell>
              <TableCell className={NUM}>{formatCurrency(totais.disponivelIbs)}</TableCell>
              <TableCell className={NUM}>{formatCurrency(totais.destacadoCbs)}</TableCell>
              <TableCell className={NUM}>{formatCurrency(totais.aApropriarCbs)}</TableCell>
              <TableCell className={NUM}>{formatCurrency(totais.disponivelCbs)}</TableCell>
              {credito.Devolucao && (
                <TableCell className={NUM}>
                  {formatCurrency(totais.estornoIbs)} / {formatCurrency(totais.estornoCbs)}
                </TableCell>
              )}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
